import { defineStore } from 'pinia'
import axios from '../config/api.js'
import { unwrapCollection, toNumber, iriId, LD_HEADERS } from './apiCollection.js'

// Statuts métier (enum backend App\Entity\CommandeStatus)
const COMMANDE_STATUSES = ['DRAFT', 'PROCESSING', 'SENT_TO_SAP', 'PROCESSED', 'PARTIALLY_CLOSED', 'CLOSED', 'ANOMALY']

// Le champ `status` n'est pas exposé par l'API (/api/commandes) : seul `validatedSystem` l'est.
// Correspondance avec les règles backend (ValidatedSystemEvaluator / CommandeStatus) :
//   success            -> PROCESSING (toutes données présentes, prête pour SAP)
//   pending_completion -> DRAFT      (données essentielles manquantes)
//   error              -> ANOMALY    (lignes invalides / problèmes détectés)
const VALIDATED_SYSTEM_TO_STATUS = {
  success: 'PROCESSING',
  pending_completion: 'DRAFT',
  error: 'ANOMALY'
}

function resolveStatus(raw) {
  const rawStatus = raw.status ?? raw.statut
  const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : null
  if (status && COMMANDE_STATUSES.includes(status)) return { status, statusSource: 'status' }
  const vs = typeof raw.validatedSystem === 'string' ? raw.validatedSystem.toLowerCase() : null
  const derived = VALIDATED_SYSTEM_TO_STATUS[vs] || null
  return { status: derived, statusSource: derived ? 'validatedSystem' : null }
}

function normalizeLigne(ligne = {}) {
  const produit = ligne.produit && typeof ligne.produit === 'object' ? ligne.produit : {}
  const quantity = toNumber(ligne.quantite)
  const unitPrice = toNumber(ligne.prix_unitaire)
  return {
    id: ligne.id,
    productName: ligne.description || produit.description_sap || produit.description_oracle || '—',
    productRef: ligne.sku || produit.codes_sap || produit.codes_oracle || '—',
    quantity,
    unitPrice,
    totalPrice: quantity !== null && unitPrice !== null ? quantity * unitPrice : null
  }
}

// Mappe une Commande API Platform vers le modèle attendu par la vue.
// Pas de date, d'email client ni de confiance IA dans l'API : null => « — ».
function normalizeCommande(raw = {}, customerNames = {}) {
  const items = Array.isArray(raw.lignes) ? raw.lignes.map(normalizeLigne) : []
  const priced = items.filter(i => i.totalPrice !== null)
  const netValue = toNumber(raw.net_value ?? raw.montant_total)
  const customerId = iriId(raw.customer)
  const embeddedName = raw.customer && typeof raw.customer === 'object' ? raw.customer.customer_name : null
  return {
    ...raw,
    id: raw.id,
    customerId,
    purchaseOrderNo: raw.purchase_order_no ?? null,
    validatedSystem: raw.validatedSystem ?? null,
    clientName: embeddedName || customerNames[customerId] || (customerId ? `Client #${customerId}` : '—'),
    clientEmail: null,
    clientPhone: null,
    clientAddress: null,
    createdAt: raw.created_at ?? raw.createdAt ?? raw.date_commande ?? null,
    ...resolveStatus(raw),
    totalAmount: netValue ?? (priced.length ? priced.reduce((sum, i) => sum + i.totalPrice, 0) : null),
    itemsCount: items.length,
    aiConfidence: toNumber(raw.aiConfidence),
    source: 'Email',
    items
  }
}

// Récupère toutes les pages d'une collection API Platform (pages fixes de 30 éléments).
async function fetchAllPages(url) {
  const first = await axios.get(url, { params: { page: 1 }, headers: LD_HEADERS })
  const { items, totalItems } = unwrapCollection(first.data)
  let all = [...items]
  if (totalItems !== null && items.length > 0 && totalItems > items.length) {
    const pages = Math.ceil(totalItems / items.length)
    const rest = await Promise.all(
      Array.from({ length: pages - 1 }, (_, i) =>
        axios.get(url, { params: { page: i + 2 }, headers: LD_HEADERS })
          .then(r => unwrapCollection(r.data).items)
      )
    )
    rest.forEach(pageItems => { all = all.concat(pageItems) })
  }
  return { items: all, totalItems: totalItems ?? all.length }
}

export const useCommandesStore = defineStore('commandes', {
  state: () => ({
    commandes: [],
    loading: false,
    error: null,
    totalItems: null,
    stats: {
      DRAFT: null,
      PROCESSING: null,
      SENT_TO_SAP: null,
      PROCESSED: null,
      PARTIALLY_CLOSED: null,
      CLOSED: null,
      ANOMALY: null,
      total: 0
    },
    filters: {
      status: '',
      dateFrom: null,
      dateTo: null,
      client: ''
    }
  }),

  getters: {
    commandesByStatus: (state) => (status) => {
      return state.commandes.filter(commande => commande.status === status)
    },
    
    pendingCommandes: (state) => {
      return state.commandes.filter(commande => commande.status === 'pending')
    },
    
    validatedCommandes: (state) => {
      return state.commandes.filter(commande => commande.status === 'validated')
    },
    
    rejectedCommandes: (state) => {
      return state.commandes.filter(commande => commande.status === 'rejected')
    },
    
    totalAmount: (state) => {
      return state.commandes.reduce((total, commande) => total + (Number(commande.totalAmount) || 0), 0)
    },
    
    averageConfidence: (state) => {
      if (state.commandes.length === 0) return 0
      const total = state.commandes.reduce((sum, commande) => sum + commande.aiConfidence, 0)
      return Math.round(total / state.commandes.length)
    },
    
    lowConfidenceCommandes: (state) => {
      return state.commandes.filter(commande => commande.aiConfidence < 60)
    }
  },

  actions: {
    async loadCommandes() {
      this.loading = true
      this.error = null
      
      try {
        // Noms clients : la commande ne porte que l'IRI du client (/api/customers/{id})
        const [{ items, totalItems }, customers] = await Promise.all([
          fetchAllPages('/api/commandes'),
          fetchAllPages('/api/customers').catch(e => {
            console.warn('Noms clients indisponibles:', e)
            return { items: [] }
          })
        ])
        const customerNames = Object.fromEntries(customers.items.map(c => [c.id, c.customer_name]))
        
        this.commandes = items.map(raw => normalizeCommande(raw, customerNames))
        this.totalItems = totalItems
        this.updateStats()
        
      } catch (error) {
        this.error = 'Erreur lors du chargement des commandes'
        console.error('Erreur loadCommandes:', error)
      } finally {
        this.loading = false
      }
    },

    updateStats() {
      // Si l'API expose le vrai `status`, tous les compteurs sont calculés.
      // Sinon (statut déduit de `validatedSystem`), seuls DRAFT / PROCESSING / ANOMALY
      // sont déductibles ; les autres restent null (affichés « — »).
      const hasRealStatus = this.commandes.some(c => c.statusSource === 'status')
      const derivable = hasRealStatus ? COMMANDE_STATUSES : Object.values(VALIDATED_SYSTEM_TO_STATUS)
      const stats = { total: this.totalItems ?? this.commandes.length }
      COMMANDE_STATUSES.forEach(s => {
        stats[s] = derivable.includes(s) ? this.commandes.filter(c => c.status === s).length : null
      })
      this.stats = stats
    },

    async validateCommande(commandeId) {
      try {
        // Simulation d'appel API
        // await axios.patch(`/api/commandes/${commandeId}/validate`)
        
        await new Promise(resolve => setTimeout(resolve, 500))
        
        const commande = this.commandes.find(c => c.id === commandeId)
        if (commande) {
          commande.status = 'validated'
          this.updateStats()
        }
        
      } catch (error) {
        this.error = 'Erreur lors de la validation'
        console.error('Erreur validateCommande:', error)
        throw error
      }
    },

    async rejectCommande(commandeId, reason = '') {
      try {
        // Simulation d'appel API
        // await axios.patch(`/api/commandes/${commandeId}/reject`, { reason })
        
        await new Promise(resolve => setTimeout(resolve, 500))
        
        const commande = this.commandes.find(c => c.id === commandeId)
        if (commande) {
          commande.status = 'rejected'
          commande.rejectionReason = reason
          this.updateStats()
        }
        
      } catch (error) {
        this.error = 'Erreur lors du rejet'
        console.error('Erreur rejectCommande:', error)
        throw error
      }
    },

    async updateCommande(commandeId, updates) {
      try {
        // Simulation d'appel API
        // await axios.patch(`/api/commandes/${commandeId}`, updates)
        
        await new Promise(resolve => setTimeout(resolve, 500))
        
        const index = this.commandes.findIndex(c => c.id === commandeId)
        if (index !== -1) {
          this.commandes[index] = { ...this.commandes[index], ...updates }
        }
        
      } catch (error) {
        this.error = 'Erreur lors de la mise à jour'
        console.error('Erreur updateCommande:', error)
        throw error
      }
    },

    async deleteCommande(commandeId) {
      try {
        // Simulation d'appel API
        // await axios.delete(`/api/commandes/${commandeId}`)
        
        await new Promise(resolve => setTimeout(resolve, 500))
        
        const index = this.commandes.findIndex(c => c.id === commandeId)
        if (index !== -1) {
          this.commandes.splice(index, 1)
          this.updateStats()
        }
        
      } catch (error) {
        this.error = 'Erreur lors de la suppression'
        console.error('Erreur deleteCommande:', error)
        throw error
      }
    },

    async exportCommandes(format = 'csv', filters = {}) {
      try {
        // Simulation d'appel API
        // const response = await axios.post('/api/commandes/export', { format, filters })
        // return response.data
        
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Simulation de génération d'export
        const filteredCommandes = this.commandes.filter(commande => {
          if (filters.status && commande.status !== filters.status) return false
          if (filters.dateFrom && new Date(commande.createdAt) < new Date(filters.dateFrom)) return false
          if (filters.dateTo && new Date(commande.createdAt) > new Date(filters.dateTo)) return false
          return true
        })
        
        return {
          filename: `commandes_${new Date().toISOString().split('T')[0]}.${format}`,
          data: filteredCommandes,
          count: filteredCommandes.length
        }
        
      } catch (error) {
        this.error = 'Erreur lors de l\'export'
        console.error('Erreur exportCommandes:', error)
        throw error
      }
    },

    async getCommandeById(commandeId) {
      try {
        // Vérifier d'abord dans le store local
        let commande = this.commandes.find(c => c.id === commandeId)
        
        if (!commande) {
          // Simulation d'appel API si pas trouvé localement
          // const response = await axios.get(`/api/commandes/${commandeId}`)
          // commande = response.data
          
          await new Promise(resolve => setTimeout(resolve, 300))
          // Retourner null si pas trouvé
          return null
        }
        
        return commande
        
      } catch (error) {
        this.error = 'Erreur lors de la récupération de la commande'
        console.error('Erreur getCommandeById:', error)
        throw error
      }
    },

    async searchCommandes(query, filters = {}) {
      try {
        // Simulation d'appel API
        // const response = await axios.get('/api/commandes/search', { params: { q: query, ...filters } })
        // return response.data
        
        await new Promise(resolve => setTimeout(resolve, 300))
        
        // Recherche locale pour la démo
        const results = this.commandes.filter(commande => {
          const searchText = `${commande.clientName} ${commande.clientEmail} ${commande.id}`.toLowerCase()
          const matchesQuery = !query || searchText.includes(query.toLowerCase())
          const matchesStatus = !filters.status || commande.status === filters.status
          
          return matchesQuery && matchesStatus
        })
        
        return results
        
      } catch (error) {
        this.error = 'Erreur lors de la recherche'
        console.error('Erreur searchCommandes:', error)
        throw error
      }
    },

    clearError() {
      this.error = null
    },

    setFilters(filters) {
      this.filters = { ...this.filters, ...filters }
    },

    clearFilters() {
      this.filters = {
        status: '',
        dateFrom: null,
        dateTo: null,
        client: ''
      }
    }
  }
})